<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Mobile Sidebar Overlay -->
    <div 
      v-if="sidebarOpen" 
      class="fixed inset-0 z-40 lg:hidden"
      @click="sidebarOpen = false"
    >
      <div class="absolute inset-0 bg-black bg-opacity-50"></div>
    </div>

    <!-- Sidebar -->
    <div 
      class="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col"
      :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <!-- Sidebar Header with Logo -->
      <div class="flex items-center justify-between h-20 px-6 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center">
          <div class="w-16 h-16 mr-4">
            <img 
              src="/logo6.png" 
              alt="Annzz Logo" 
              class="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 class="text-xl font-bold text-gray-900">ANNZZ</h1>
            <p class="text-xs text-gray-500">Admin Panel</p>
          </div>
        </div>
        
        <!-- Mobile Menu Button -->
        <button 
          @click="sidebarOpen = false"
          class="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- User Info Button -->
      <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <button
          @click="router.push('/admin/users')"
          class="w-full flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-200 border border-gray-200 hover:border-orange-300 hover:shadow-sm"
        >
          <UserDisplay />
        </button>
      </div>

      <!-- Contact Us Button -->
      <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <button
          @click="showContactModal = true"
          class="w-full flex items-center justify-center px-4 py-3 rounded-lg text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 border border-gray-200 hover:border-orange-300 hover:shadow-sm"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Us
        </button>
      </div>

      <!-- Sidebar Navigation -->
      <nav class="flex flex-col flex-1 overflow-y-auto">
        <!-- Main Navigation Items -->
        <div class="flex-1 px-4 pt-8">
          <div class="space-y-2">
            <!-- Dashboard -->
            <router-link 
              to="/admin" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/admin' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
              </svg>
              Dashboard
            </router-link>
            
            <!-- Users & Staff -->
            <router-link 
              to="/admin/users" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/admin/users' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              Users & Staff
            </router-link>
            
            <!-- Products -->
            <router-link 
              to="/admin/products" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/admin/products' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Products
            </router-link>
            
            <!-- Labels -->
            <router-link 
              to="/admin/labels" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/admin/labels' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Labels
            </router-link>
            
            <!-- Pending Orders -->
            <router-link 
              to="/admin/orders" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/admin/orders' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Pending Orders
            </router-link>
            
            <!-- Workshop Jobs -->
            <router-link 
              to="/admin/workshop" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/admin/workshop' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Workshop Jobs
            </router-link>
            
            
            <!-- Invites -->
            <router-link 
              to="/admin/invites" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/admin/invites' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              Invites
            </router-link>
          </div>
        </div>


        <!-- Bottom Section with Logout -->
        <div class="px-4 pb-6 flex-shrink-0">
          <!-- Logout Button -->
          <button 
            @click="handleLogout"
            class="w-full group flex items-center px-4 py-3 rounded-lg text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      </nav>
    </div>

    <!-- Main Content Area -->
    <div class="lg:pl-64">
      <!-- Mobile Menu Button -->
      <div class="lg:hidden fixed top-4 left-4 z-30">
        <button 
          @click="sidebarOpen = true"
          class="p-3 bg-white rounded-lg shadow-lg border border-gray-200 hover:bg-gray-100 transition-all duration-200"
          aria-label="Open admin sidebar menu"
          title="Open admin sidebar menu"
        >
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      <!-- Page Content -->
      <div class="p-4 sm:p-6 lg:p-8 pt-20 lg:pt-4">
        <slot />
      </div>
    </div>

    <!-- Contact Us Modal -->
    <ContactUsModal 
      :show="showContactModal" 
      @close="showContactModal = false" 
    />
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import ContactUsModal from './ContactUsModal.vue'
import UserDisplay from './UserDisplay.vue'

export default {
  name: 'AdminLayout',
  components: {
    UserDisplay,
    ContactUsModal
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const userStore = useUserStore()
    const sidebarOpen = ref(false)
    const showContactModal = ref(false)

    const pageTitle = computed(() => {
      const path = route.path
      if (path === '/admin') return 'Dashboard'
      if (path === '/admin/users') return 'Users & Staff'
      if (path === '/admin/products') return 'Products'
      if (path === '/admin/orders') return 'Pending Orders'
      if (path === '/admin/workshop') return 'Workshop Jobs'
      return 'Admin'
    })

    const handleLogout = async () => {
      try {
        await userStore.signOut()
        router.push('/')
      } catch (error) {
        console.error('Logout failed:', error)
      }
    }

    return {
      sidebarOpen,
      showContactModal,
      pageTitle,
      handleLogout,
      router
    }
  }
}
</script> 